import { useState } from 'react'
import { Button, Drawer, Input, ProductPicker } from '@/components/ui'
import type { PickerValue } from '@/components/ui/ProductPicker/ProductPicker'
import { useAddReviewLine } from '../api/reviewQueries'

export interface AddReviewLineDrawerProps {
  reviewId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Add a product that is not on the customer's pricelist yet, or another
 * quantity tier of one that is. It is created in Odoo only when an admin
 * approves the review.
 */
export function AddReviewLineDrawer({ reviewId, open, onOpenChange }: AddReviewLineDrawerProps) {
  const add = useAddReviewLine()
  const [product, setProduct] = useState<PickerValue>(null)
  const [minQty, setMinQty] = useState('0')
  const [part, setPart] = useState('')

  function close() {
    setProduct(null)
    setMinQty('0')
    setPart('')
    add.reset()
    onOpenChange(false)
  }

  function submit() {
    if (!product) return
    const qty = Number(minQty)
    if (Number.isNaN(qty) || qty < 0) return
    add.mutate(
      { reviewId, line: { product_tmpl_id: product[0], min_quantity: qty, customer_part_number: part.trim() || null } },
      { onSuccess: close },
    )
  }

  const qtyInvalid = minQty.trim() === '' || Number.isNaN(Number(minQty)) || Number(minQty) < 0

  return (
    <Drawer
      surface="form"
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      size="wide"
      title="Add a product"
      meta="Adds a line to this review with a blank new price. It reaches the Odoo pricelist only when an admin approves."
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={close} disabled={add.isPending}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={submit} disabled={!product || qtyInvalid || add.isPending}>
            {add.isPending ? 'Adding…' : 'Add line'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-role-section-label">Product</span>
          <ProductPicker value={product} onChange={setProduct} placeholder="Search products…" excludeSamples />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-role-section-label">Minimum quantity</span>
          <Input inputMode="decimal" value={minQty} onChange={(e) => setMinQty(e.target.value)} />
          <span className="text-role-metadata">0 for the base price; a higher number adds a quantity tier.</span>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-role-section-label">Customer part # (optional)</span>
          <Input value={part} onChange={(e) => setPart(e.target.value)} />
        </label>
        {add.isError ? <p className="m-0 text-sm text-danger">{(add.error as Error).message}</p> : null}
      </div>
    </Drawer>
  )
}
